import { Flex, Text } from '@chakra-ui/react';

import { formatCurrency } from 'app/utils/strings';
import { getPointsValue } from 'app/utils/points';

interface ReachSummaryProps {
  /** How many prides the campaign runs in as it stands. */
  prideCount: number;
  /** The award each winner takes, or nothing where the campaign has no award. */
  awardPoints?: number;
  /** Whether the prides are resolved afresh each run, which is what makes the count a snapshot. */
  resolvedEachRun: boolean;
}

/**
 * What the campaign reaches, and what that costs, as it is being set up.
 * <p>
 * The two are one question: an award is paid once per pride, so how many prides it runs in is what decides
 * whether the funding pot has to cover one award or forty. Shown while the form is being filled in rather than
 * after it is submitted, since the numbers move as the choices are made.
 */
function ReachSummary({ prideCount, awardPoints = undefined, resolvedEachRun }: ReachSummaryProps) {
  const committed = (awardPoints ?? 0) * prideCount;

  return (
    <Flex
      flexDir="column"
      gap="4px"
      bg="brand.lightGray2"
      borderRadius="5px"
      p="12px 16px"
      data-testid="reach-summary"
    >
      <Text textStyle="smallMedium" color="brand.black">
        {prideCount === 1 ? 'Runs in 1 pride' : `Runs in ${prideCount} prides`}
        {awardPoints
          ? ` · commits ${committed.toLocaleString('en-GB')} pts (${formatCurrency(
              getPointsValue(committed),
              'GBP'
            )})`
          : ' · no award, so nothing is committed'}
      </Text>
      {resolvedEachRun && (
        <Text textStyle="extraSmallRegular" color="brand.darkGray">
          The prides are worked out again every run, so this is the count today. A later run may
          reach more prides and commit more than this.
        </Text>
      )}
    </Flex>
  );
}

export default ReachSummary;
